import React, { useEffect, useMemo, useState } from 'react';

import * as S from './styles';

import DocumentDegree from './components/DocumentDegree';
import { ButtonSimple } from '../../../../../components/newStudent/Buttons';
import BulletPoint from '../../../../../components/newStudent/BulletPoint';
import {
  typeDocComplementary,
  typeDocInternship,
} from '../../../../../variables';
import ButtonOpen from '../../../../../components/newStudent/Buttons/ButtonOpen';
import type { IDocumentsDegree, IUniversityDegree } from '../../../../../types/newStudent/documents.interface';
import { handleMaxWidth } from '../../../../../util/newStudent';
import {
  normalizeDegreeItemsForPortfolio,
  resolveComplementarySectionStatus,
  resolveInternshipSectionStatus,
  sumHoursDegree,
} from '../../../../../util/newStudent/documentsDegree';
import { useTheme } from 'styled-components';
import { BasicDialog } from '../../../../../components/newStudent/Dialog';
import DocumentDegreeMobile from './components/DocumentDegreeMobile';

interface Props {
  data: IUniversityDegree;
}
export const defaultDataUniversity = {
  id: '',
  name: '',
  justification: '',
  startDate: '',
  endDate: '',
  workload: '',
  description: '',
  typeActivity: '',
  file: undefined,
  url: '',
  checked: undefined,
  situation: 'aguardando' as 'aguardando' | 'aprovado' | 'reprovado' | 'Concluído',
  registrationId: 0,
  universityDegreeId: 0,
};
export default function UniversityDegree({ data }: Props) {
  const [oldDataInternship, setOldDataInternship] = useState<
    IDocumentsDegree[] | []
  >([]);
  const [oldDataComplementaryActivity, setOldDataComplementaryActivity] =
    useState<IDocumentsDegree[] | []>([]);

  const [dataInternship, setDataInternship] = useState<IDocumentsDegree[] | []>(
    [defaultDataUniversity],
  );
  const [dataComplementaryActivity, setDataComplementaryActivity] = useState<
    IDocumentsDegree[] | []
  >([defaultDataUniversity]);

  const [isOpenInternship, setIsOpenInternship] = useState<
    number | undefined
  >();
  const [isOpenComplementaryActivity, setIsOpenComplementaryActivity] =
    useState<number | undefined>();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);

  const theme = useTheme();

  const internshipSectionStatus = resolveInternshipSectionStatus(data);
  const complementarySectionStatus = resolveComplementarySectionStatus(data);
  const internshipMetaOk = internshipSectionStatus === 'concluido';
  const complementaryMetaOk = complementarySectionStatus === 'concluido';

  const internshipUi = useMemo(
    () => normalizeDegreeItemsForPortfolio(data.internship, internshipMetaOk),
    [data.internship, internshipMetaOk],
  );
  const complementaryUi = useMemo(
    () =>
      normalizeDegreeItemsForPortfolio(
        data.complementaryActivity,
        complementaryMetaOk,
      ),
    [data.complementaryActivity, complementaryMetaOk],
  );

  useEffect(() => {
    setOldDataInternship(internshipUi);
    setOldDataComplementaryActivity(complementaryUi);
  }, [internshipUi, complementaryUi]);

  /** Cores alinhadas ao status agregado retornado pela API (pendente / andamento / concluido). */
  const internshipOpenColor =
    internshipSectionStatus === 'concluido'
      ? 'success'
      : internshipSectionStatus === 'andamento'
        ? 'primary'
        : 'danger';

  const complementaryOpenColor =
    complementarySectionStatus === 'concluido'
      ? 'success'
      : complementarySectionStatus === 'andamento'
        ? 'primary'
        : 'danger';

  return (
    <S.Container>
      <>
        <BulletPoint
          color={internshipOpenColor}
          buttons={
            <S.Buttons>
              <ButtonSimple
                noButton
                color={internshipOpenColor}
                outline
                heightSize="small"
                widthSize="xmedium"
              >
                {internshipSectionStatus === 'pendente'
                  ? 'Pendente'
                  : internshipSectionStatus === 'concluido'
                    ? 'Concluído'
                    : 'Andamento'}
              </ButtonSimple>
              <ButtonOpen
                color={internshipOpenColor}
                heightSize="small"
                widthSize="medium"
                onClick={() => {
                  setIsOpenInternship(
                    isOpenInternship !== data.courseId
                      ? data.courseId
                      : undefined,
                  );
                  handleMaxWidth(theme.size?.medium) && setIsOpenModal(true);
                }}
                title="Abrir"
                titleClose="Fechar"
                open={isOpenInternship === data.courseId}
              />
            </S.Buttons>
          }
          flexDirection={handleMaxWidth(theme.size?.medium) ? 'column' : 'row'}
        >
          <div>
            <S.DocumentName>
              Estágio{handleMaxWidth(theme.size?.medium) ? <br /> : ' | '}
              {data.courseName}
            </S.DocumentName>
            {data.internship.length > 0 && (
              <S.InfoHours>
                <strong>Horas Aguardando:</strong>{' '}
                {sumHoursDegree(data.internship).horasAguardando}
                <br />
                <strong>Horas Aprovadas:</strong>{' '}
                {sumHoursDegree(data.internship).horasAprovadas}
              </S.InfoHours>
            )}
          </div>
        </BulletPoint>
        {isOpenInternship &&
          (handleMaxWidth(theme.size?.medium) ? (
            <BasicDialog
              fullScreen
              isOpen={isOpenModal}
              setIsOpen={setIsOpenModal}
            >
              <DocumentDegreeMobile
                title="Estágio"
                courseName={data.courseName}
                setIsOpenModal={setIsOpenModal}
                newData={dataInternship}
                registrationId={data.registrationId}
                oldData={oldDataInternship}
                setNewData={setDataInternship}
                documentDegreeType={+typeDocInternship!}
                universityDegreeId={data.universityDegreeId}
                setWhoIsOpen={setIsOpenInternship}
                setOldData={setOldDataInternship}
                inProgress={data.internship.length > 0}
                degreeMetaComplete={internshipMetaOk}
              />
            </BasicDialog>
          ) : (
            <DocumentDegree
              registrationId={data.registrationId}
              universityDegreeId={data.universityDegreeId}
              oldData={oldDataInternship}
              courseId={data.courseId}
              open={isOpenInternship}
              setNewData={setDataInternship}
              setOldData={setOldDataInternship}
              newData={dataInternship}
              documentDegreeType={+typeDocInternship!}
              degreeMetaComplete={internshipMetaOk}
            />
          ))}
      </>
      <>
        <BulletPoint
          color={complementaryOpenColor}
          buttons={
            <S.Buttons>
              <ButtonSimple
                noButton
                color={complementaryOpenColor}
                outline
                heightSize="small"
                widthSize="xmedium"
              >
                {complementarySectionStatus === 'pendente'
                  ? 'Pendente'
                  : complementarySectionStatus === 'concluido'
                    ? 'Concluído'
                    : 'Andamento'}
              </ButtonSimple>
              <ButtonOpen
                color={complementaryOpenColor}
                heightSize="small"
                widthSize="medium"
                onClick={() => {
                  setIsOpenComplementaryActivity(
                    isOpenComplementaryActivity !== data.courseId
                      ? data.courseId
                      : undefined,
                  );

                  handleMaxWidth(theme.size?.medium) && setIsOpenModal(true);
                }}
                title="Abrir"
                titleClose="Fechar"
                open={isOpenComplementaryActivity === data.courseId}
              />
            </S.Buttons>
          }
          flexDirection={handleMaxWidth(theme.size?.medium) ? 'column' : 'row'}
        >
          <div>
            <S.DocumentName>
              Atividade complementar
              {handleMaxWidth(theme.size?.medium) ? <br /> : ' | '}
              {data.courseName}
            </S.DocumentName>
            {data.complementaryActivity.length > 0 && (
              <S.InfoHours>
                <strong>Horas Aguardando:</strong>{' '}
                {sumHoursDegree(data.complementaryActivity).horasAguardando}
                <br />
                <strong>Horas Aprovadas:</strong>{' '}
                {sumHoursDegree(data.complementaryActivity).horasAprovadas}
              </S.InfoHours>
            )}
          </div>
        </BulletPoint>
        {isOpenComplementaryActivity &&
          (handleMaxWidth(theme.size?.medium) ? (
            <BasicDialog
              fullScreen
              isOpen={isOpenModal}
              setIsOpen={setIsOpenModal}
            >
              <DocumentDegreeMobile
                title="Atividade complementar"
                courseName={data.courseName}
                setIsOpenModal={setIsOpenModal}
                newData={dataComplementaryActivity}
                registrationId={data.registrationId}
                oldData={oldDataComplementaryActivity}
                setNewData={setDataComplementaryActivity}
                documentDegreeType={+typeDocComplementary!}
                universityDegreeId={data.universityDegreeId}
                setWhoIsOpen={setIsOpenComplementaryActivity}
                setOldData={setOldDataComplementaryActivity}
                inProgress={data.complementaryActivity.length > 0}
                degreeMetaComplete={complementaryMetaOk}
              />
            </BasicDialog>
          ) : (
            <DocumentDegree
              registrationId={data.registrationId}
              universityDegreeId={data.universityDegreeId}
              oldData={oldDataComplementaryActivity}
              courseId={data.courseId}
              open={isOpenComplementaryActivity}
              setNewData={setDataComplementaryActivity}
              setOldData={setOldDataComplementaryActivity}
              newData={dataComplementaryActivity}
              documentDegreeType={+typeDocComplementary!}
              degreeMetaComplete={complementaryMetaOk}
            />
          ))}
      </>
    </S.Container>
  );
}
